import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { parseTurnCode, getHexColor } from '@/lib/turn-color';

interface WordHighlighterProps {
  text: string;
  turnCodes: Record<string, string>;
  colorMappings: any[];
  phase: '1' | '2';
  currentWordIndex: number;
  fontSize?: number;
  enabledColors?: Set<string>; // Color filtering - undefined = show all
}

interface TurnSegment {
  type: 'text' | 'color';
  content: string;
  color?: string;
}

// Preserve capitalization from original word
function preserveCapitalization(
  segmentContent: string,
  originalWord: string,
  segmentIndex: number,
  allSegments: TurnSegment[]
): string {
  let position = 0;
  for (let i = 0; i < segmentIndex; i++) {
    position += allSegments[i].content.length;
  }

  if (position >= 0 && position + segmentContent.length <= originalWord.length) {
    return originalWord.substring(position, position + segmentContent.length);
  }

  return segmentContent;
}

export const WordHighlighter: React.FC<WordHighlighterProps> = ({
  text,
  turnCodes,
  colorMappings,
  phase,
  currentWordIndex,
  fontSize = 24,
  enabledColors
}) => {
  // Color filtering logic - matches SimpleTurnColorRenderer and PDFTextRenderer
  const isColorEnabled = (colorName: string): boolean => {
    if (!enabledColors) return true; // undefined = show all
    return enabledColors.has(colorName);
  };
  // Parse text into word tokens
  const tokens = useMemo(() => {
    const result: { type: 'word' | 'space' | 'punctuation'; content: string; wordIndex?: number }[] = [];
    // Include smart/curly quotes: " " ' ' and other common punctuation
    const regex = /(\s+)|([.,!?;:'"()\[\]{}—–\-""''«»‹›„‚]+)|([a-zA-Z']+)/g;
    let match;
    let wordIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      if (match[1]) {
        // Whitespace
        result.push({ type: 'space', content: match[1] });
      } else if (match[2]) {
        // Punctuation (including smart quotes)
        result.push({ type: 'punctuation', content: match[2] });
      } else if (match[3]) {
        // Word
        result.push({ type: 'word', content: match[3], wordIndex });
        wordIndex++;
      }
    }

    return result;
  }, [text]);

  // Render a word with turn color coding
  const renderWord = (word: string, isHighlighted: boolean, wordIndex: number) => {
    const turnCode = turnCodes[word.toLowerCase()];
    const segments = turnCode ? parseTurnCode(turnCode) : null;

    const wordStyle: React.CSSProperties = {
      fontSize: `${fontSize}px`,
      fontFamily: 'var(--font-family, system-ui)',
      lineHeight: 1.6,
      display: 'inline-flex',
      alignItems: 'center',
      gap: `${fontSize * 0.03}px`,
      transition: 'all 0.15s ease-out',
      ...(isHighlighted && {
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        borderRadius: '4px',
        padding: '0 4px',
        transform: 'scale(1.05)',
      })
    };

    if (!segments || segments.length === 0) {
      // No turn code - render plain text
      return (
        <motion.span
          key={wordIndex}
          style={wordStyle}
          animate={isHighlighted ? { scale: 1.05 } : { scale: 1 }}
        >
          {word}
        </motion.span>
      );
    }

    return (
      <motion.span
        key={wordIndex}
        style={wordStyle}
        animate={isHighlighted ? { scale: 1.05 } : { scale: 1 }}
      >
        {segments.map((segment, segIndex) => {
          if (segment.type === 'text') {
            const originalSegment = preserveCapitalization(segment.content, word, segIndex, segments);
            return (
              <span key={segIndex} className="text-gray-900">
                {originalSegment}
              </span>
            );
          } else if (segment.type === 'color' && segment.color) {
            const originalSegment = preserveCapitalization(segment.content, word, segIndex, segments);
            
            // Check if this color is enabled
            if (!isColorEnabled(segment.color)) {
              // Disabled color: render as plain text (no Turn styling)
              return (
                <span key={segIndex} className="text-gray-900">
                  {originalSegment}
                </span>
              );
            }
            
            // Enabled color: render with Turn styling
            const hexColor = getHexColor(segment.color, colorMappings);

            if (phase === '1') {
              // Phase 1: Colored squares
              return (
                <span
                  key={segIndex}
                  className="inline-block rounded-sm"
                  style={{
                    backgroundColor: hexColor,
                    width: `${fontSize * 0.55}px`,
                    height: `${fontSize * 0.55}px`,
                    verticalAlign: 'baseline',
                    margin: '0 1px'
                  }}
                />
              );
            } else {
              // Phase 2: Colored underlines
              return (
                <span key={segIndex} className="relative">
                  <span className="text-gray-900">{originalSegment}</span>
                  <div
                    className="absolute bottom-0 left-0 right-0 rounded-sm"
                    style={{
                      backgroundColor: hexColor,
                      height: `${fontSize * 0.1}px`
                    }}
                  />
                </span>
              );
            }
          }
          return null;
        })}
      </motion.span>
    );
  };

  return (
    <div 
      className="leading-relaxed text-gray-900"
      style={{ fontSize: `${fontSize}px` }}
    >
      {tokens.map((token, index) => {
        if (token.type === 'space') {
          return <span key={index}>{token.content}</span>;
        } else if (token.type === 'punctuation') {
          return (
            <span 
              key={index} 
              style={{ fontSize: `${fontSize}px` }}
            >
              {token.content}
            </span>
          );
        } else {
          const isHighlighted = token.wordIndex === currentWordIndex;
          return renderWord(token.content, isHighlighted, token.wordIndex!);
        }
      })}
    </div>
  );
};
